<template>
	<HorizontalSplit>
		<template #top>
			<div style="position: relative; width: 100%; height: 100%">
				<mgl-map :map-style="mapStyleURL(ALIDADE_SMOOTH)" @map:load="onMapLoad">
					<template v-if="task">
						<mgl-raster-source
							source-id="stadia-satellite"
							:tiles="stadiaTileURLs"
							:tile-size="256"
							:maxzoom="19"
						>
							<mgl-raster-layer layer-id="stadia-satellite" />
						</mgl-raster-source>

						<mgl-geo-json-source source-id="tile-bounds" :data="task.geometry">
							<mgl-line-layer
								layer-id="tile-bounds-outline"
								:paint="{
									'line-color': '#ffc107',
									'line-width': 2.5,
								}"
							/>
						</mgl-geo-json-source>

						<!-- The planet item's ortho imagery, as the review base layer -->
						<mgl-raster-source
							:key="`planet-${task.itemId}`"
							:source-id="`planet-${task.itemId}`"
							:tiles="planetTileURLs"
							:tile-size="256"
							:maxzoom="19"
						>
							<mgl-raster-layer
								before="tile-bounds-outline"
								:layer-id="`planet-${task.itemId}`"
							/>
						</mgl-raster-source>

						<!-- Pools already found intersecting this tile (non-fatal fetch) -->
						<mgl-geo-json-source
							v-if="foundPoolsData"
							source-id="found-pools"
							:data="foundPoolsData"
						>
							<mgl-fill-layer
								before="tile-bounds-outline"
								layer-id="found-pools-fill"
								:layout="{
									visibility: showFoundPools ? 'visible' : 'none',
								}"
								:paint="{
									'fill-color': '#7c3aed',
									'fill-opacity': 0.2,
								}"
							/>
							<mgl-line-layer
								before="tile-bounds-outline"
								layer-id="found-pools-outline"
								:layout="{
									visibility: showFoundPools ? 'visible' : 'none',
								}"
								:paint="{
									'line-color': '#7c3aed',
									'line-width': 1.5,
								}"
							/>
						</mgl-geo-json-source>

						<!-- Reviewer-flagged findings on this tile (non-fatal fetch) -->
						<mgl-geo-json-source
							v-if="findingsData"
							source-id="findings"
							:data="findingsData"
						>
							<mgl-fill-layer
								before="tile-bounds-outline"
								layer-id="findings-fill"
								:layout="{
									visibility: showFindings ? 'visible' : 'none',
								}"
								:paint="{
									'fill-color': '#dc3545',
									'fill-opacity': 0.15,
								}"
							/>
							<mgl-line-layer
								before="tile-bounds-outline"
								layer-id="findings-outline"
								:layout="{
									visibility: showFindings ? 'visible' : 'none',
								}"
								:paint="{
									'line-color': '#dc3545',
									'line-width': 1.5,
								}"
							/>
						</mgl-geo-json-source>

						<!-- Rough polygon the reviewer draws to flag a finding -->
						<MglTerradrawSource
							v-if="showDrawTool"
							ref="terradrawRef"
							source-id="finding-drawn"
							:modes="drawToolModes"
							v-model="drawnFeatures"
							@create="onTerradrawCreate"
							@update="onTerradrawUpdate"
						/>
					</template>
				</mgl-map>

				<div class="map-overlay-controls">
					<button
						class="map-overlay-control"
						:class="{ 'is-active': showFoundPools }"
						title="Toggle found-pools overlay (O)"
						:aria-pressed="showFoundPools"
						@click="showFoundPools = !showFoundPools"
					>
						<i
							class="bi"
							:class="showFoundPools ? 'bi-droplet-fill' : 'bi-droplet'"
						></i>
					</button>
					<button
						class="map-overlay-control"
						:class="{ 'is-active': showFindings }"
						title="Toggle flagged-findings overlay (F)"
						:aria-pressed="showFindings"
						@click="showFindings = !showFindings"
					>
						<i
							class="bi"
							:class="showFindings ? 'bi-flag-fill' : 'bi-flag'"
						></i>
					</button>
					<button
						class="map-overlay-control"
						:class="{ 'is-active': showDrawTool }"
						title="Toggle draw tool (D)"
						:aria-pressed="showDrawTool"
						@click="toggleDrawTool"
					>
						<i
							class="bi"
							:class="showDrawTool ? 'bi-pencil-fill' : 'bi-pencil'"
						></i>
					</button>
					<button
						class="map-overlay-control"
						:disabled="drawnFeatures.length === 0"
						title="Delete all drawn areas"
						@click="clearDrawnPolygon"
					>
						<i class="bi bi-trash"></i>
					</button>
				</div>
			</div>
		</template>

		<template #bottom>
			<div
				v-if="loading"
				class="d-flex align-items-center justify-content-center h-100 text-muted"
			>
				<div class="text-center">
					<div class="spinner-border text-primary mb-2" role="status"></div>
					<p class="small">Loading review tile…</p>
				</div>
			</div>

			<div v-else-if="error" class="p-3">
				<div class="alert alert-danger">{{ error }}</div>
			</div>

			<div
				v-else-if="done"
				class="d-flex align-items-center justify-content-center h-100 text-muted"
			>
				<div class="text-center">
					<div class="fs-3 mb-2"><i class="bi bi-check-circle"></i></div>
					<p>All review tiles are done. Nice work.</p>
					<RouterLink
						to="/_/sudo/vision-tasks"
						class="btn btn-outline-secondary btn-sm"
					>
						<i class="bi bi-arrow-left"></i> Back to Vision Tasks
					</RouterLink>
				</div>
			</div>

			<div v-else-if="task" class="p-3">
				<div
					v-if="foundPoolsError"
					class="alert alert-warning alert-dismissible small py-2"
				>
					<i class="bi bi-exclamation-triangle me-1"></i>
					Could not load already-found pools: {{ foundPoolsError }}
					<button
						type="button"
						class="btn-close"
						aria-label="Dismiss"
						@click="foundPoolsError = ''"
					></button>
				</div>

				<div
					v-if="findingsError"
					class="alert alert-warning alert-dismissible small py-2"
				>
					<i class="bi bi-exclamation-triangle me-1"></i>
					Could not load flagged findings: {{ findingsError }}
					<button
						type="button"
						class="btn-close"
						aria-label="Dismiss"
						@click="findingsError = ''"
					></button>
				</div>

				<div class="d-flex justify-content-between align-items-center mb-3">
					<h6 class="mb-0">
						<i class="bi bi-bounding-box-circles me-1"></i>
						Tile Confirmation
						<span class="text-muted">#{{ task.id }}</span>
					</h6>
					<div class="d-flex align-items-center gap-2">
						<span
							class="badge"
							:class="task.hasPool ? 'text-bg-warning' : 'text-bg-secondary'"
							title="Corpus pre-mark, not a human verdict"
						>
							{{ task.hasPool ? "Known pool" : "No known pools" }}
						</span>
						<RouterLink
							to="/_/sudo/vision-tasks"
							class="btn btn-outline-secondary btn-sm"
						>
							<i class="bi bi-arrow-left"></i> Back
						</RouterLink>
					</div>
				</div>

				<div class="row g-3">
					<div class="col-md-6">
						<h6>
							<i class="bi bi-clipboard-check me-1"></i>
							Condition
						</h6>
						<div class="mb-3">
							<div class="d-flex align-items-center gap-2 mb-2">
								<span class="fw-semibold small" style="width: 90px"
									>(H)as pools?</span
								>
								<div
									class="btn-group btn-group-sm"
									role="group"
									aria-label="Has pools"
								>
									<button
										type="button"
										class="btn"
										:class="hasPools ? 'btn-success' : 'btn-outline-secondary'"
										@click="hasPools = true"
									>
										Yes
									</button>
									<button
										type="button"
										class="btn"
										:class="!hasPools ? 'btn-success' : 'btn-outline-secondary'"
										@click="hasPools = false"
									>
										No
									</button>
								</div>
							</div>
							<div class="d-flex align-items-center gap-2">
								<span class="fw-semibold small" style="width: 90px"
									>Is (C)orrect?</span
								>
								<div
									class="btn-group btn-group-sm"
									role="group"
									aria-label="Is correct"
								>
									<button
										type="button"
										class="btn"
										:class="isCorrect ? 'btn-success' : 'btn-outline-secondary'"
										:disabled="openFindings.length > 0"
										title="Flagged findings must be fixed (resolved or discarded) before this tile can be marked correct"
										@click="isCorrect = true"
									>
										Yes
									</button>
									<button
										type="button"
										class="btn"
										:class="!isCorrect ? 'btn-danger' : 'btn-outline-secondary'"
										@click="isCorrect = false"
									>
										No
									</button>
								</div>
							</div>
						</div>
						<div
							v-if="openFindings.length > 0"
							class="alert alert-warning small py-2 mb-3"
						>
							<i class="bi bi-flag me-1"></i>
							{{ openFindings.length }} open finding(s) — this tile is marked
							not correct until they are fixed and it is re-reviewed.
						</div>
						<div
							v-if="showCorrectionPrompt"
							class="alert alert-info small py-2 mb-3"
						>
							<i class="bi bi-pencil-square me-1"></i>
							The tile is incorrect. Draw over the incorrect areas to record
							corrections.
						</div>
						<button
							class="btn btn-primary btn-sm mb-3"
							:disabled="busy"
							@click="save"
						>
							<i class="bi bi-check-lg me-1"></i>
							Save
							<span class="ms-1"><kbd>S</kbd>/<kbd>Enter</kbd></span>
						</button>
						<h6>
							<i class="bi bi-info-circle me-1"></i>
							Layer details
						</h6>
						<table class="table table-sm small mb-3">
							<tbody>
								<tr>
									<th class="text-muted" style="width: 140px">Planet item</th>
									<td class="text-break">{{ task.itemId }}</td>
								</tr>
								<tr>
									<th class="text-muted">Asset</th>
									<td>{{ task.assetType }}</td>
								</tr>
								<tr>
									<th class="text-muted">Tile size</th>
									<td>{{ task.tileSize }} px</td>
								</tr>
								<tr>
									<th class="text-muted">Tile origin</th>
									<td>x={{ task.x }}, y={{ task.y }}</td>
								</tr>
								<tr>
									<th class="text-muted">Status</th>
									<td>{{ task.status }}</td>
								</tr>
								<tr v-if="task.reviewedAt">
									<th class="text-muted">Reviewed</th>
									<td>{{ formatDate(task.reviewedAt) }}</td>
								</tr>
							</tbody>
						</table>
						<p class="text-muted small mb-0">
							The map shows this planet item's ortho imagery. Use the yellow box
							as the boundary of the tile you are judging.
						</p>
					</div>

					<div class="col-md-6">
						<h6>
							<i class="bi bi-keyboard me-1"></i>
							Keyboard shortcuts
						</h6>
						<div v-if="foundPools.length > 0" class="small text-muted mb-2">
							<i class="bi bi-geo-alt me-1"></i>
							{{ foundPools.length }} pool(s) already found
						</div>
						<div v-if="findings.length > 0" class="small text-muted mb-2">
							<i class="bi bi-flag me-1"></i>
							{{ findings.length }} flagged finding(s)
						</div>
						<div class="d-flex flex-column gap-2">
							<button
								class="btn btn-outline-danger"
								:disabled="busy || drawnPolygon === null"
								@click="markFinding('false_negative')"
							>
								<kbd>M</kbd> — Missing Pool
								<small class="text-muted d-block mt-1">
									A pool exists here but was not found. Draw around it, then
									flag.
								</small>
							</button>
							<button
								class="btn btn-outline-warning"
								:disabled="busy || drawnPolygon === null"
								@click="markFinding('false_positive')"
							>
								<kbd>N</kbd> — Incorrect Pool
								<small class="text-muted d-block mt-1">
									A drawn pool here is not actually a pool. Draw around it, then
									flag.
								</small>
							</button>
						</div>
					</div>
				</div>
			</div>
		</template>
	</HorizontalSplit>

	<ToastNotification
		:show="toastShow"
		:title="toastTitle"
		:message="toastMessage"
	/>

	<Teleport to="body">
		<div v-if="showEmptyConfirmModal">
			<div class="modal-backdrop fade show"></div>
			<div
				class="modal fade show d-block"
				tabindex="-1"
				@click.self="showEmptyConfirmModal = false"
			>
				<div class="modal-dialog modal-dialog-centered">
					<div class="modal-content">
						<div class="modal-header">
							<h5 class="modal-title">
								<i
									class="bi bi-exclamation-triangle-fill text-warning me-1"
								></i>
								Confirm empty tile
							</h5>
							<button
								type="button"
								class="btn-close"
								@click="showEmptyConfirmModal = false"
							></button>
						</div>
						<div class="modal-body">
							<p>
								This tile is pre-marked as containing a known pool (<span
									class="badge text-bg-warning"
									>Known pool</span
								>).
							</p>
							<p class="mb-0">
								Marking it <strong>Pool-free</strong> records the whole tile as
								a pool-free region, which can overlap that pool. Only confirm if
								you are sure this tile contains no pool.
							</p>
						</div>
						<div class="modal-footer">
							<button
								type="button"
								class="btn btn-outline-secondary"
								@click="showEmptyConfirmModal = false"
							>
								Cancel
							</button>
							<button
								type="button"
								class="btn btn-danger"
								:disabled="busy"
								@click="doConfirmEmpty()"
							>
								Confirm Pool-free
							</button>
						</div>
					</div>
				</div>
			</div>
		</div>
	</Teleport>
</template>

<script setup lang="ts">
import {
	computed,
	nextTick,
	onBeforeUnmount,
	onMounted,
	ref,
	watch,
} from "vue";
import { useRoute, useRouter } from "vue-router";
import {
	MglMap,
	MglRasterSource,
	MglRasterLayer,
	MglGeoJsonSource,
	MglFillLayer,
	MglLineLayer,
	useMap,
} from "vue-maplibre-gl";
import HorizontalSplit from "@/components/layout/HorizontalSplit.vue";
import MglTerradrawSource from "@/components/MglTerradrawSource.vue";
import ToastNotification from "@/components/ToastNotification.vue";
import { ALIDADE_SMOOTH } from "@/map/constants";
import { mapStyleURL, boundsFromGeoJSON } from "@/map/util";
import { apiClient } from "@/client";
import type {
	ReviewFinding,
	ReviewTileTask,
	VisionReviewTileDetail,
	VisionReviewTilePool,
} from "@/type/api";

const route = useRoute();
const router = useRouter();
const mapInstance = useMap();

const task = ref<ReviewTileTask | null>(null);
const loading = ref(false);
const busy = ref(false);
const error = ref("");
const done = ref(false);
const showEmptyConfirmModal = ref(false);

// Pools already found intersecting this tile (found-pools overlay). The fetch
// is non-fatal: a failure only shows a dismissible note and never blocks the
// verdict buttons.
const foundPools = ref<VisionReviewTilePool[]>([]);
const foundPoolsError = ref("");
// Whether the found-pools map overlay is visible (toggle button + O key).
const showFoundPools = ref(true);

// Reviewer-flagged findings on this tile (findings overlay). The fetch is
// non-fatal, mirroring loadFoundPools.
const findings = ref<ReviewFinding[]>([]);
const findingsError = ref("");
// Findings not yet fixed. A pending/in_progress finding means the tile's pool
// assessment is not fully correct, so the tile stays marked not-correct until
// the finding is fixed (resolved/discarded) and the tile is re-reviewed.
const openFindings = computed(() =>
	findings.value.filter(
		(f) => f.status === "pending" || f.status === "in_progress",
	),
);
// Whether the findings map overlay is visible (toggle button + F key).
const showFindings = ref(true);

// Rough area the reviewer draws to flag a finding (draw tool). Toggled with
// the map button or the D key; rectangle mode is re-armed on enable. The
// terradraw toolbar is limited to rectangle (draw) and select (move/edit);
// deleting is handled by the "delete all" map control.
const drawToolModes: string[] = ["render", "rectangle", "select"];
const showDrawTool = ref(false);
const terradrawRef = ref<InstanceType<typeof MglTerradrawSource> | null>(null);
const drawnFeatures = ref<GeoJSON.Feature[]>([]);
// The last Polygon/MultiPolygon captured from drawnFeatures on terradraw
// create/update — the geometry posted when marking a finding.
const drawnPolygon = ref<GeoJSON.Geometry | null>(null);
// Reviewer's two-part condition verdict: whether the tile has pools, and
// whether the marked pools are all correct. Derived into a pool_verdict
// (pool_free / partial / all) on save. Initialized from the corpus pre-mark
// and reset when a tile loads.
const hasPools = ref(false);
// Reviewer's manual correctness judgement. The effective isCorrect is forced
// to false while any finding on this tile is still open (pending/in_progress):
// a flagged incorrect or missing pool means the assessment is not correct
// until the finding is fixed and the tile re-reviewed. Seeded per tile in
// load().
const isCorrectManual = ref(true);
const isCorrect = computed({
	get: () => (openFindings.value.length > 0 ? false : isCorrectManual.value),
	set: (v: boolean) => {
		isCorrectManual.value = v;
	},
});

// Toast feedback for flagging (mirrors PositionInferenceAnchoring.vue).
const toastShow = ref(false);
const toastTitle = ref("");
const toastMessage = ref("");

const taskId = computed(() =>
	route.params.taskId ? String(route.params.taskId) : "",
);

const stadiaTileURLs = ["/api/tile/stadia/alidade_satellite/{z}/{x}/{y}"];

const planetTileURLs = computed(() =>
	task.value
		? [
				`/api/tile/planet/any/{z}/{x}/{y}?bands=3,2,1&source=${encodeURIComponent(
					task.value.itemId,
				)}`,
			]
		: [],
);

// FeatureCollection of the found pools for the map overlay.
const foundPoolsData = computed<GeoJSON.GeoJSON | null>(() => {
	if (foundPools.value.length === 0) return null;
	return {
		type: "FeatureCollection",
		features: foundPools.value
			.filter((p) => p.geometry)
			.map((p) => ({
				type: "Feature" as const,
				geometry: p.geometry as GeoJSON.Geometry,
				properties: { id: p.id },
			})),
	};
});

// FeatureCollection of the flagged findings for the map overlay.
const findingsData = computed<GeoJSON.GeoJSON | null>(() => {
	if (findings.value.length === 0) return null;
	return {
		type: "FeatureCollection",
		features: findings.value
			.filter((f) => f.geometry)
			.map((f) => ({
				type: "Feature" as const,
				geometry: f.geometry as GeoJSON.Geometry,
				properties: { id: f.id },
			})),
	};
});

// Prompt the reviewer to draw over incorrect areas only when the tile has
// pools, they've marked it incorrect, and no correction has been drawn or
// flagged yet.
const showCorrectionPrompt = computed(
	() =>
		hasPools.value &&
		!isCorrect.value &&
		drawnPolygon.value === null &&
		findings.value.length === 0,
);

function showToast(title: string, message: string) {
	toastTitle.value = title;
	toastMessage.value = message;
	toastShow.value = true;
	setTimeout(() => {
		toastShow.value = false;
	}, 5000);
}

// Capture the last Polygon/MultiPolygon feature drawn in the terradraw layer
// as the finding geometry. Handlers are wired to the terradraw create/update
// events; the v-model keeps drawnFeatures current.
function captureDrawnPolygon() {
	drawnPolygon.value = null;
	for (const f of drawnFeatures.value) {
		if (f.geometry.type === "Polygon" || f.geometry.type === "MultiPolygon") {
			drawnPolygon.value = f.geometry;
		}
	}
}

function onTerradrawCreate(_feature: any) {
	// The v-model sync lands synchronously after the create event; capture on
	// the next tick so drawnFeatures holds the just-finished feature.
	void nextTick(captureDrawnPolygon);
}

function onTerradrawUpdate(_feature: any) {
	void nextTick(captureDrawnPolygon);
}

function clearDrawnPolygon() {
	drawnFeatures.value = [];
	drawnPolygon.value = null;
}

function toggleDrawTool() {
	showDrawTool.value = !showDrawTool.value;
	if (showDrawTool.value) {
		// The component mounts on the next tick (v-if); arm the rectangle tool
		// once it is attached.
		void nextTick(() => terradrawRef.value?.switchMode("rectangle"));
	}
}

function formatDate(iso: string): string {
	if (!iso) return "\u2014";
	return new Date(iso).toLocaleString("en-US", {
		month: "short",
		day: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	});
}

async function load() {
	loading.value = true;
	error.value = "";
	done.value = false;
	try {
		if (taskId.value) {
			task.value = await apiClient.JSONGet<ReviewTileTask>(
				`/api/vision/review-task/${encodeURIComponent(taskId.value)}`,
			);
		} else {
			const next = await apiClient.JSONGet<ReviewTileTask | null>(
				"/api/vision/review-task/next",
			);
			if (next) {
				task.value = next;
				await router.replace(
					`/_/sudo/vision-tasks/tile-confirmation/${next.id}`,
				);
			} else {
				task.value = null;
				done.value = true;
			}
		}
		if (task.value) {
			// Seed the condition toggles from the corpus pre-mark.
			hasPools.value = task.value.hasPool;
			isCorrect.value = true;
		}
	} catch (e: any) {
		error.value = e?.message ?? "Failed to load review tile.";
	} finally {
		loading.value = false;
	}
}

async function advance() {
	busy.value = false;
	// Re-claim the next pending tile; the URL change reloads the task.
	const next = await apiClient.JSONGet<ReviewTileTask | null>(
		"/api/vision/review-task/next",
	);
	if (next) {
		await router.replace(`/_/sudo/vision-tasks/tile-confirmation/${next.id}`);
	} else {
		task.value = null;
		done.value = true;
	}
}

// The pool_verdict implied by the two condition toggles: no pools -> pool_free,
// pools + correct -> all, pools + incorrect -> partial.
function derivedVerdict(): "pool_free" | "partial" | "all" {
	if (!hasPools.value) return "pool_free";
	return isCorrect.value ? "all" : "partial";
}

// Finalize the tile review from the condition toggles and advance. A
// pool-free verdict also records a negative region, guarded by a confirmation
// when the tile was pre-marked as containing a known pool.
async function save() {
	if (!task.value || busy.value) return;
	const verdict = derivedVerdict();
	if (verdict === "pool_free") {
		if (task.value.hasPool) {
			showEmptyConfirmModal.value = true;
			return;
		}
		await doConfirmEmpty();
		return;
	}
	busy.value = true;
	try {
		await apiClient.JSONPut(`/api/vision/review-task/${task.value.id}`, {
			status: "reviewed",
			poolVerdict: verdict,
		});
		await advance();
	} catch (e: any) {
		error.value = e?.message ?? "Failed to save the verdict.";
		busy.value = false;
	}
}

async function doConfirmEmpty() {
	if (!task.value || busy.value) return;
	showEmptyConfirmModal.value = false;
	busy.value = true;
	try {
		// Record the tile as a confirmed pool-free region for this item.
		await apiClient.JSONPost("/api/vision/train/negative-region", {
			geometry: task.value.geometry,
			itemId: task.value.itemId,
		});
		await apiClient.JSONPut(`/api/vision/review-task/${task.value.id}`, {
			status: "reviewed",
			poolVerdict: "pool_free",
		});
		await advance();
	} catch (e: any) {
		error.value = e?.message ?? "Failed to confirm empty.";
		busy.value = false;
	}
}

// Slippy-tile math (Web Mercator), mirroring tileToLng/tileToLat in
// VisionTaskReview.vue.
function tileToLng(tx: number, tz: number): number {
	return (tx / Math.pow(2, tz)) * 360 - 180;
}

function tileToLat(ty: number, tz: number): number {
	const latRad = Math.atan(
		Math.sinh(Math.PI * (1 - (2 * ty) / Math.pow(2, tz))),
	);
	return (latRad * 180) / Math.PI;
}

// The highest-zoom slippy tile whose bounds fully contain the given bbox: the
// tightest tile around the task, so the found-pools fetch stays local. If no
// tile fits (degenerate bbox), fall back to the center tile at z=22.
function tileForBBox(
	west: number,
	south: number,
	east: number,
	north: number,
): { z: number; x: number; y: number } {
	const centerLng = (west + east) / 2;
	const centerLat = (south + north) / 2;
	for (let z = 22; z >= 0; z--) {
		const n = Math.pow(2, z);
		const x = Math.floor(((centerLng + 180) / 360) * n);
		const y = Math.floor(
			((1 - Math.asinh(Math.tan((centerLat * Math.PI) / 180)) / Math.PI) / 2) *
				n,
		);
		if (
			tileToLng(x, z) <= west &&
			tileToLng(x + 1, z) >= east &&
			tileToLat(y, z) >= north &&
			tileToLat(y + 1, z) <= south
		) {
			return { z, x, y };
		}
	}
	const n = Math.pow(2, 22);
	return {
		z: 22,
		x: Math.floor(((centerLng + 180) / 360) * n),
		y: Math.floor(
			((1 - Math.asinh(Math.tan((centerLat * Math.PI) / 180)) / Math.PI) / 2) *
				n,
		),
	};
}

// Fetch pools intersecting this task's tile for the map overlay. Non-fatal:
// a failure only shows a dismissible note and never blocks the verdicts.
async function loadFoundPools() {
	const t = task.value;
	if (!t) return;
	foundPools.value = [];
	foundPoolsError.value = "";
	const bounds = boundsFromGeoJSON(t.geometry);
	if (!bounds) return;
	const tile = tileForBBox(
		bounds.getWest(),
		bounds.getSouth(),
		bounds.getEast(),
		bounds.getNorth(),
	);
	try {
		const detail = await apiClient.JSONGet<VisionReviewTileDetail>(
			`/api/vision/review/item/${encodeURIComponent(t.itemId)}/${tile.z}/${tile.x}/${tile.y}`,
		);
		foundPools.value = detail.pools ?? [];
	} catch (e: any) {
		foundPoolsError.value = e?.message ?? "Failed to load already-found pools.";
	}
}

// Fetch reviewer-flagged findings for this tile for the map overlay.
// Non-fatal, mirroring loadFoundPools.
async function loadFindings() {
	const t = task.value;
	if (!t) return;
	findings.value = [];
	findingsError.value = "";
	try {
		findings.value =
			(await apiClient.JSONGet<ReviewFinding[]>(
				`/api/vision/review/finding?review_tile_task_id=${encodeURIComponent(t.id)}`,
			)) ?? [];
	} catch (e: any) {
		findingsError.value = e?.message ?? "Failed to load flagged findings.";
	}
}

// Record a reviewer finding for the drawn polygon ([ missing pool,
// ] incorrect pool). The drawn area itself identifies the pool, so no pool id
// is attached.
async function markFinding(kind: "false_negative" | "false_positive") {
	const t = task.value;
	if (!t || busy.value) return;
	const geometry = drawnPolygon.value;
	if (!geometry) return;
	busy.value = true;
	try {
		const body: {
			kind: "false_negative" | "false_positive";
			geometry: GeoJSON.Geometry;
			itemId: string;
			reviewTileTaskId: number;
		} = {
			kind,
			geometry,
			itemId: t.itemId,
			reviewTileTaskId: t.id,
		};
		await apiClient.JSONPost("/api/vision/review/finding", body);
		// A flagged error means the tile's assessment is not correct until the
		// finding is fixed (resolved/discarded) and the tile re-reviewed. The
		// computed isCorrect also stays false while the finding is open.
		isCorrect.value = false;
		showToast(
			kind === "false_negative"
				? "Missing pool flagged"
				: "Incorrect pool flagged",
			"Finding recorded for this tile.",
		);
		clearDrawnPolygon();
		await loadFindings();
	} catch (e: any) {
		showToast("Flag failed", e?.message ?? "Failed to record the finding.");
	} finally {
		busy.value = false;
	}
}

function onKeydown(e: KeyboardEvent) {
	if (e.metaKey || e.ctrlKey || e.altKey) return;
	// Ignore typing in inputs/textarea/select.
	const t = e.target as HTMLElement | null;
	if (
		t &&
		(t.tagName === "INPUT" ||
			t.tagName === "TEXTAREA" ||
			t.tagName === "SELECT")
	) {
		return;
	}
	// While the empty-confirm modal is open, Enter confirms, Escape cancels.
	if (showEmptyConfirmModal.value) {
		if (e.key === "Enter") void doConfirmEmpty();
		else if (e.key === "Escape") showEmptyConfirmModal.value = false;
		return;
	}
	const key = e.key.toLowerCase();
	if (key === "s" || key === "enter") void save();
	else if (key === "h") hasPools.value = !hasPools.value;
	else if (key === "c") isCorrect.value = !isCorrect.value;
	else if (key === "o") showFoundPools.value = !showFoundPools.value;
	else if (key === "m") void markFinding("false_negative");
	else if (key === "n") void markFinding("false_positive");
	else if (key === "d" && !busy.value) toggleDrawTool();
	else if (key === "f" && !busy.value) showFindings.value = !showFindings.value;
}

function onMapLoad() {
	fitToTask();
}

function fitToTask() {
	const map = mapInstance.map;
	if (!map || !task.value) return;
	const bounds = boundsFromGeoJSON(task.value.geometry);
	if (bounds) {
		map.fitBounds(bounds, { padding: 40, duration: 0 });
	}
}

// Fit the viewport and load the found-pools + findings overlays once the map
// and/or task become available (either order).
watch(task, () => {
	fitToTask();
	loadFoundPools();
	loadFindings();
	// A new tile starts with a clean slate: no drawn polygon.
	clearDrawnPolygon();
});

watch(taskId, load, { immediate: true });

onMounted(() => {
	window.addEventListener("keydown", onKeydown);
});
onBeforeUnmount(() => {
	window.removeEventListener("keydown", onKeydown);
});
</script>

<style scoped>
/* Map overlay controls for the found-pools, findings, and draw-tool toggles. */
.map-overlay-controls {
	position: absolute;
	top: 10px;
	right: 10px;
	z-index: 10;
	display: flex;
	flex-direction: column;
	gap: 6px;
}
.map-overlay-control {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 32px;
	height: 32px;
	border: 1px solid rgba(0, 0, 0, 0.2);
	border-radius: 4px;
	background: #fff;
	color: #495057;
	font-size: 1rem;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
	cursor: pointer;
}
.map-overlay-control:hover {
	background: #f8f9fa;
}
.map-overlay-control.is-active {
	color: #7c3aed;
	border-color: #7c3aed;
}
</style>
